import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './StripePages.css';
import { ensureFirebaseApp } from '../utils/mobileWebAuth';

// Try to open the mobile app via its custom URL scheme WITHOUT navigating the
// current page away — see ReturnPage.js for the full rationale (a plain
// window.location.href inside a timer, with no direct user click, is blocked
// or only partially handled by several mobile browsers and can leave the tab
// on a blank page if the app doesn't actually open).
function tryOpenAppSilently(url) {
  try {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = url;
    document.body.appendChild(iframe);
    setTimeout(() => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    }, 2000);
  } catch (e) {
    console.error('[SubscriptionSuccess] Silent app-open attempt failed:', e);
  }
}

function SubscriptionSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const source = searchParams.get('source') === 'mobile' ? 'mobile' : 'web';
  const appReturnUrl = searchParams.get('app_return') || 'fodyfee://stripe/return';
  const returnToken = (searchParams.get('return_token') || '').trim();
  const isMobileFlow = source === 'mobile';

  useEffect(() => {
    ensureFirebaseApp();
    const auth = getAuth();

    const verifySubscription = async (currentUser) => {
      const sessionId = searchParams.get('session_id');

      if (!sessionId) {
        setStatus('error');
        setErrorMessage('Missing session information.');
        return;
      }

      if (!currentUser && !(source === 'mobile' && returnToken)) {
        // SECURITY: verifyCheckoutSession requires a verified Firebase Auth
        // session OR a valid signed mobile return token — it never trusts a
        // userId taken from the URL. The webhook is still the source of truth
        // and will complete the subscription server-side regardless of what
        // this page can show.
        setStatus('error');
        setErrorMessage('Could not verify your session. Please open the app and check your subscription status.');
        return;
      }

      try {
        // Verify the checkout session directly with Stripe via backend.
        // The backend identifies the account from request.auth.uid when a
        // session exists, or from the signed returnToken for the mobile
        // external-browser flow — never from a client-supplied userId.
        const functions = getFunctions();
        const verifyCheckoutSession = httpsCallable(functions, 'verifyCheckoutSession');
        const payload = currentUser ? { sessionId } : { sessionId, returnToken };
        const result = await verifyCheckoutSession(payload);
        const data = result.data;

        if (data.success) {
          setSubscriptionData({
            name: data.productName || 'Subscription',
            description: data.productDescription,
            status: data.status || 'active',
            startDate: data.startDate,
            endDate: data.endDate,
          });
          setStatus('success');
        } else {
          setStatus('error');
          setErrorMessage(data.message || 'Payment was not completed.');
        }
      } catch (error) {
        console.error('Error verifying subscription:', error);
        setStatus('error');
        setErrorMessage(error.message || 'Failed to verify subscription. Please contact support.');
      }
    };

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      verifySubscription(currentUser);
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, source, returnToken]);

  // Auto-attempt to return to the app after a successful verification. For
  // mobile this uses a hidden-iframe attempt instead of navigating the
  // visible page away, so a "Return to Mobile App" button click always
  // remains available as a reliable fallback if the silent attempt is
  // blocked by the browser.
  useEffect(() => {
    if (status === 'success') {
      const timer = setTimeout(() => {
        if (isMobileFlow) {
          tryOpenAppSilently(appReturnUrl);
        } else {
          navigate('/admin/dashboard');
        }
      }, isMobileFlow ? 900 : 2500);
      return () => clearTimeout(timer);
    }
  }, [status, navigate, isMobileFlow, appReturnUrl]);

  const handleGoToDashboard = () => {
    if (isMobileFlow) {
      window.location.href = appReturnUrl;
      return;
    }
    navigate('/admin/dashboard');
  };

  const floatingIngredients = (
    <div className="ff-hero-ingredients">
      <div className="ff-ingredient tomato"></div>
      <div className="ff-ingredient pepper"></div>
      <div className="ff-ingredient mushroom"></div>
      <div className="ff-ingredient leaf"></div>
      <div className="ff-ingredient onion"></div>
      <div className="ff-ingredient carrot"></div>
      <div className="ff-ingredient garlic"></div>
      <div className="ff-ingredient broccoli"></div>
      <div className="ff-ingredient banana"></div>
      <div className="ff-ingredient bread"></div>
      <div className="ff-ingredient egg"></div>
      <div className="ff-ingredient sausage"></div>
      <div className="ff-ingredient cheese"></div>
      <div className="ff-ingredient lemon"></div>
      <div className="ff-ingredient strawberry"></div>
    </div>
  );

  if (status === 'loading') {
    return (
      <div className="stripe-page loading" style={{ position: 'relative', overflow: 'hidden' }}>
        {floatingIngredients}
        <div className="stripe-card">
          <div className="loading-spinner"></div>
          <h1>Confirming Your Subscription...</h1>
          <p>Please wait while we verify your payment with Stripe.</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="stripe-page error" style={{ position: 'relative', overflow: 'hidden' }}>
        {floatingIngredients}
        <div className="stripe-card">
          <div className="error-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M15 9l-6 6M9 9l6 6" />
            </svg>
          </div>
          <h1>Something Went Wrong</h1>
          <p>{errorMessage}</p>
          <button onClick={handleGoToDashboard} className="primary-button">
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="stripe-page success" style={{ position: 'relative', overflow: 'hidden' }}>
      {floatingIngredients}
      <div className="stripe-card">
        <div className="success-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M8 12l2 2 4-4" />
          </svg>
        </div>
        <h1>Subscription Active!</h1>
        <p>Your subscription has been successfully activated.</p>

        {subscriptionData && (
          <div className="subscription-details">
            <div className="detail-row">
              <span className="label">Plan:</span>
              <span className="value">{subscriptionData.name}</span>
            </div>
            <div className="detail-row">
              <span className="label">Status:</span>
              <span className="value status-active">{subscriptionData.status}</span>
            </div>
          </div>
        )}

        <button onClick={handleGoToDashboard} className="primary-button">
          {isMobileFlow ? 'Return to Mobile App' : 'Go to Dashboard'}
        </button>

        <p className="help-text">
          {isMobileFlow
            ? 'Redirecting to your mobile app...'
            : 'Redirecting to dashboard automatically...'}
        </p>
      </div>
    </div>
  );
}

export default SubscriptionSuccessPage;
